import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useActiveProperty } from '@/lib/property-store';
import { usePost } from '@/lib/queries';

/** Resolve a deep link before property-specific editor state or queries can mount. */
export function PostPropertyBoundary({ children }: { children: ReactNode }) {
  const [params] = useSearchParams();
  const postId = params.get('post');
  const { active, properties, setActive } = useActiveProperty();
  const post = usePost(postId);
  const aligned = useRef<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const propertyId = post.data?.propertyId;
  const unavailable =
    !!propertyId &&
    propertyId !== active.id &&
    !properties.some((property) => property.id === propertyId);
  useEffect(() => {
    if (!postId) {
      aligned.current = null;
      return;
    }
    if (!propertyId || unavailable || aligned.current === postId) return;
    if (propertyId === active.id) {
      aligned.current = postId;
      return;
    }
    // Align only on entry. A later selector change intentionally leaves this post;
    // Shell clears its URL after Save/Discard, and Stay must keep the draft mounted.
    aligned.current = postId;
    void setActive(propertyId).catch(() => setError('Could not open this post’s property.'));
  }, [postId, propertyId, active.id, unavailable, setActive]);

  if (!postId) return children;
  if (unavailable) return <p role="alert">This post’s property is unavailable.</p>;
  if (post.error || error) return <p role="alert">{error ?? 'Could not load this post.'}</p>;
  if (!propertyId || propertyId !== active.id) return <p role="status">Loading post…</p>;
  return children;
}
